import { useCallback } from 'react';
import { HierarchyManager } from '../services/HierarchyManager.js';
import { PrefabManager } from '../services/PrefabManager.js';
import { SelectionManager } from '../services/SelectionManager.js';
import { measureNode, calculateRect, deepCloneNodes, getNodeChildren } from '../utils.js';

// Helper to check if a node is a descendant of another
const isDescendantOf = (node, ancestorId, allNodes) => {
  let current = node;
  while (current.parentId) {
    if (current.parentId === ancestorId) return true;
    current = allNodes.find(n => n.id === current.parentId);
    if (!current) break;
  }
  return false;
};

export const useNodeOperations = ({
  activeNodes,
  selectedNodeIds,
  prefabs,
  rootW,
  rootH,
  historyExecute,
  targetPrefabId,
  selectedPrefabId,
  setPrefabs,
  setSelectedPrefabId,
  setNodes,
  setSelectedNodeIds,
  createCompositeCommand,
  createUpdatePrefabsCommand,
  createUpdateNodesCommand,
  executeCommand,
  containerNode
}) => {
  const handleDelete = useCallback((idsToDelete) => {
    if (idsToDelete.length === 0) return;
    const newNodes = HierarchyManager.deleteNodes(idsToDelete, activeNodes);
    historyExecute(activeNodes, newNodes, selectedNodeIds, []);
  }, [activeNodes, selectedNodeIds, historyExecute]);

  const handleDuplicate = useCallback(() => {
    if (selectedNodeIds.length === 0) return;
    const result = HierarchyManager.duplicateNodes(
      selectedNodeIds,
      activeNodes,
      deepCloneNodes
    );
    if (result) {
      historyExecute(activeNodes, result.newNodes, selectedNodeIds, result.newSelection);
    }
  }, [selectedNodeIds, activeNodes, historyExecute]);

  const handleMakePrefab = useCallback((targetNodeIds, name) => {
    if (targetNodeIds.length === 0) return;

    const result = PrefabManager.createPrefab(targetNodeIds, name, activeNodes, prefabs, rootW, rootH);
    if (!result) return;

    const { prefabDef, instanceNodes, nodesToRemove } = result;
    const newPrefabs = [...prefabs, prefabDef];

    // Update the nodes in place (merging new props/links) to preserve order
    const newNodes = activeNodes.map(n => {
        const updated = instanceNodes.find(inNode => inNode.id === n.id);
        return updated || n;
    });

    const cmd = createCompositeCommand([
      createUpdatePrefabsCommand(prefabs, newPrefabs, selectedPrefabId, null, {
        setPrefabs,
        setSelectedPrefabId
      }),
      createUpdateNodesCommand(targetPrefabId, activeNodes, newNodes, selectedNodeIds, [instanceNodes[0].id], {
        setNodes,
        setPrefabs,
        setSelectedNodeIds
      })
    ]);

    executeCommand(cmd);
  }, [
    activeNodes,
    prefabs,
    rootW,
    rootH,
    selectedPrefabId,
    targetPrefabId,
    selectedNodeIds,
    createCompositeCommand,
    createUpdatePrefabsCommand,
    createUpdateNodesCommand,
    executeCommand,
    setPrefabs,
    setSelectedPrefabId,
    setNodes,
    setSelectedNodeIds
  ]);

  const handleDetachPrefab = useCallback((instanceId) => {
    const result = PrefabManager.detachPrefab(instanceId, activeNodes, prefabs);
    if (!result) return;

    const { newNodes, newRootId, instanceId: instId } = result;
    const newNodesList = [...activeNodes.filter(n => n.id !== instId), ...newNodes];
    historyExecute(activeNodes, newNodesList, selectedNodeIds, [newRootId]);
  }, [activeNodes, prefabs, selectedNodeIds, historyExecute]);

  const handleUpdatePrefab = useCallback((instanceId) => {
    const result = PrefabManager.updatePrefabFromInstance(instanceId, activeNodes, prefabs);
    if (!result) return;

    const { prefabId, updatedNodes } = result;
    setPrefabs(prev => prev.map(p => (p.id === prefabId ? { ...p, nodes: updatedNodes } : p)));
  }, [activeNodes, prefabs, setPrefabs]);

  // Helper function to track changed properties in dot notation
  const getChangedProperties = (updates) => {
    const changedProps = [];

    if (updates.props) {
      Object.keys(updates.props).forEach(key => {
        changedProps.push(`props.${key}`);
      });
    }

    // Track direct properties
    Object.keys(updates).forEach(key => {
      if (key !== 'props') {
        changedProps.push(key);
      }
    });

    return changedProps;
  };

  const handlePropertyUpdate = useCallback((updates, targetIds) => {
    const targets = targetIds || selectedNodeIds;
    if (targets.length === 0) return;

    // Split targets into Real and Virtual
    const realTargetIds = [];
    const virtualTargets = [];

    targets.forEach(id => {
        if (id.includes(':')) virtualTargets.push(id);
        else realTargetIds.push(id);
    });

    let newNodes = [...activeNodes];

    // 1. Update Real Nodes
    if (realTargetIds.length > 0) {
        newNodes = newNodes.map(n => {
          if (!realTargetIds.includes(n.id)) return n;
          const resolvedUpdates = typeof updates === 'function' ? updates(n) : updates;

          // Handle props merging with proper undefined deletion
          let newProps = n.props;
          if (resolvedUpdates.props) {
            newProps = { ...n.props, ...resolvedUpdates.props };
            // Delete properties with undefined values
            Object.keys(newProps).forEach(key => {
              if (newProps[key] === undefined) {
                delete newProps[key];
              }
            });
          }

          // Check if this is a prefab instance child (has sourceNodeId)
          // If so, track the changed properties in instanceOverrides
          if (n.props?.sourceNodeId) {
            // Find actual changes by comparing old props with new props
            const changedPaths = [];

            if (resolvedUpdates.props) {
              Object.keys(resolvedUpdates.props).forEach(key => {
                const oldValue = n.props[key];
                const newValue = resolvedUpdates.props[key];

                // Only track if value actually changed
                if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                  // Special handling for nested objects like responsiveOverrides
                  if (key === 'responsiveOverrides' && typeof oldValue === 'object' && typeof newValue === 'object') {
                    // Track specific breakpoint changes
                    const oldOverrides = oldValue || {};
                    const newOverrides = newValue || {};

                    // Find which breakpoints changed
                    const allBreakpoints = new Set([...Object.keys(oldOverrides), ...Object.keys(newOverrides)]);
                    allBreakpoints.forEach(bp => {
                      const oldBpOverrides = oldOverrides[bp] || {};
                      const newBpOverrides = newOverrides[bp] || {};

                      // Find which properties changed in this breakpoint
                      const changedProps = new Set([...Object.keys(oldBpOverrides), ...Object.keys(newBpOverrides)]);
                      changedProps.forEach(prop => {
                        if (JSON.stringify(oldBpOverrides[prop]) !== JSON.stringify(newBpOverrides[prop])) {
                          changedPaths.push(`props.responsiveOverrides.${bp}.${prop}`);
                        }
                      });
                    });
                  } else {
                    changedPaths.push(`props.${key}`);
                  }
                }
              });
            }

            // Track direct property changes (not props object)
            Object.keys(resolvedUpdates).forEach(key => {
              if (key !== 'props' && key !== 'id') { // Skip id since it never changes
                if (JSON.stringify(n[key]) !== JSON.stringify(resolvedUpdates[key])) {
                  changedPaths.push(key);
                }
              }
            });

            if (changedPaths.length > 0) {
              const currentInstanceOverrides = newProps.instanceOverrides || [];
              const newInstanceOverrides = [...new Set([...currentInstanceOverrides, ...changedPaths])];

              newProps = {
                ...newProps,
                instanceOverrides: newInstanceOverrides
              };
            }
          }

          return { ...n, ...resolvedUpdates, props: newProps };
        });
    }

    // 2. Update Virtual Nodes (Store in Instance Overrides)
    if (virtualTargets.length > 0) {
        // Group updates by Instance ID
        const instanceUpdates = {};

        virtualTargets.forEach(vid => {
            const [instanceId, internalId] = vid.split(':');
            if (!instanceUpdates[instanceId]) instanceUpdates[instanceId] = {};

            // Note: If 'updates' is a function, we pass a mock because we don't have easy access
            // to the virtual node state here without expensive lookup.
            // In most cases (PropertyPanel, Hierarchy), updates is an object.
            const resolvedUpdates = typeof updates === 'function' ? updates({ id: internalId, props: {} }) : updates;

            instanceUpdates[instanceId][internalId] = resolvedUpdates;
        });

        newNodes = newNodes.map(n => {
            if (instanceUpdates[n.id]) {
                const updatesForInstance = instanceUpdates[n.id];
                const currentOverrides = n.props.overrides || {};
                const newOverrides = { ...currentOverrides };

                Object.entries(updatesForInstance).forEach(([internalId, update]) => {
                     const prevOverride = newOverrides[internalId] || {};
                     const mergedUpdate = { ...prevOverride, ...update };

                     // Deep merge props
                     if (update.props) {
                         mergedUpdate.props = { ...(prevOverride.props || {}), ...update.props };
                         // Clean undefined
                         Object.keys(mergedUpdate.props).forEach(key => {
                             if (mergedUpdate.props[key] === undefined) delete mergedUpdate.props[key];
                         });
                     }

                     newOverrides[internalId] = mergedUpdate;
                });

                return {
                  ...n,
                  props: {
                    ...n.props,
                    overrides: newOverrides
                  }
                };
            }
            return n;
        });

        // Track instanceOverrides on individual child nodes (not on root instance)
        // Find all child nodes that are part of the prefab instances and mark their changed properties
        const prefabInstanceIds = Object.keys(instanceUpdates);

        newNodes = newNodes.map(n => {
            // Check if this node is a child of any prefab instance that was updated
            const instanceId = prefabInstanceIds.find(instId => {
                // Check if this node has a sourceNodeId and is part of this instance
                return n.props?.sourceNodeId && activeNodes.some(an =>
                    an.id === instId &&
                    activeNodes.some(child => child.id === n.id && isDescendantOf(child, instId, activeNodes))
                );
            });

            if (instanceId && instanceUpdates[instanceId][n.props.sourceNodeId]) {
                const update = instanceUpdates[instanceId][n.props.sourceNodeId];
                const changedPaths = getChangedProperties(update);
                const currentInstanceOverrides = n.props.instanceOverrides || [];
                const newInstanceOverrides = [...new Set([...currentInstanceOverrides, ...changedPaths])];

                return {
                  ...n,
                  props: {
                    ...n.props,
                    instanceOverrides: newInstanceOverrides
                  }
                };
            }
            return n;
        });
    }

    historyExecute(activeNodes, newNodes, selectedNodeIds, selectedNodeIds);
  }, [selectedNodeIds, activeNodes, historyExecute]);

  const handleNodeDoubleClick = useCallback((e, id, isLiveMode, isSpacePressed, containerNode, pushContext, clearSelection) => {
    if (isLiveMode || isSpacePressed) return;

    const targetId = SelectionManager.resolveContextualNodeId(id, activeNodes, containerNode);
    const node = activeNodes.find(n => n.id === targetId);
    const hasChildren = node ? getNodeChildren(activeNodes, targetId).length > 0 : false;

    if (node && (node.type === 'Box' || node.props.prefabId || hasChildren)) {
      e.stopPropagation();

      let width, height;
      let contextId = targetId; // Default to the node itself

      // For prefab instances, push the prefab definition ID instead
      if (node.props.prefabId) {
        const prefabDef = prefabs.find(p => p.id === node.props.prefabId);
        if (prefabDef) {
          // Push the prefab definition ID with the instance ID as metadata
          contextId = prefabDef.id;
          width = prefabDef.defaultWidth;
          height = prefabDef.defaultHeight;
          pushContext(contextId, width, height, targetId); // Pass instance ID
        } else {
          // Fallback to measured dimensions if no prefab found
          const rect = measureNode(targetId, activeNodes, rootW, rootH);
          width = rect.width;
          height = rect.height;
          pushContext(contextId, width, height);
        }
      } else {
        // For regular boxes, use measured dimensions
        const rect = measureNode(targetId, activeNodes, rootW, rootH);
        width = rect.width;
        height = rect.height;
        pushContext(contextId, width, height);
      }

      clearSelection();
    }
  }, [activeNodes, containerNode, rootW, rootH, prefabs]);

  return {
    handleDelete,
    handleDuplicate,
    handleMakePrefab,
    handleDetachPrefab,
    handleUpdatePrefab,
    handlePropertyUpdate,
    handleNodeDoubleClick
  };
};
